import { VELA } from '@somnhold/content';
import { iatan2, idiv, imax, isqrt, mulBp, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, UnsleepingState } from '../../types.ts';
import { damageHero, damageStructure, ev } from '../combat.ts';
import { at, by, crowd, heroesNear, held, holdable, hurt, isTower, nearestHero, said, standing, type Kit } from './kit.ts';
import { freeAt, heroesOnLine, leapAway, onLine, shoot, stood, trackStill } from './ranged.ts';

/*
 * Vela, the Archer without arrows (scout): she shoots smoke off an empty string. Every shot goes on through the heroes
 * behind her target and lands harder on one that stood still; a smoke arrow through a line of heroes leaves them
 * smoked (the nightmares hit them harder); a hero closing in, she leaps away and leaves black roots that hold; and
 * the Endless rain falls on the tightest group (two smaller rains in her last phase), on heroes and towers alike.
 */

// cds: smoke arrow, leap's recharge. k: leap charges. busy: the leap's steps (vx, vy each).
const ARROW = 0;
const LEAP = 1;
const CHARGES = 0;

/** Her blow on one hero: at least a share of its health, more on one that stood still. */
const shot = (h: Hero, dmg: number): number => {
  const d = by(h, dmg, VELA.blowMaxHpBp);
  return stood(h, VELA.still.ticks) ? mulBp(d, VELA.still.bp) : d;
};

/** The smoke arrow along the line that crosses the most heroes. */
const arrow = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const a = VELA.arrow;
  let best: Hero[] = [];
  let tgt: Hero | null = null;
  for (const h of s.heroes) {
    if (!standing(h) || !withinRange(e.x, e.y, h.x, h.y, a.range)) continue;
    const line = heroesOnLine(s, e, h.x, h.y, a.range, a.width);
    if (line.length > best.length || (line.length === best.length && h.pid === u.hunt)) {
      best = line;
      tgt = h;
    }
  }
  if (!tgt) return false;
  const dx = tgt.x - e.x;
  const dy = tgt.y - e.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  for (const h of best) {
    hurt(s, e, u, h, a.damage, a.maxHpBp);
    if (standing(h)) h.unSmoke = a.ticks;
  }
  u.cds[ARROW] = a.every;
  e.facing = iatan2(dy, dx);
  e.anim = 'attack';
  e.animT = 0;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: e.x + idiv(dx * a.range, d), ty: e.y + idiv(dy * a.range, d), kind: 'velaArrow' });
  said(s, e, u, 'Arrow', tgt.x, tgt.y);
  return true;
};

/** A hero too close: she leaps away, leaving black roots where she stood. */
const leap = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const l = VELA.leap;
  const max = l.charges[e.phase] ?? 1;
  if ((u.k[CHARGES] ?? 0) < max && u.cds[LEAP] === 0) {
    u.k[CHARGES] = (u.k[CHARGES] ?? 0) + 1;
    if ((u.k[CHARGES] as number) < max) u.cds[LEAP] = l.every;
  }
  if ((u.k[CHARGES] ?? 0) <= 0) return false;
  const h = nearestHero(s, e, l.near);
  if (!h || !leapAway(s, e, u, h.x, h.y, l.far, l.step)) return false;
  u.k[CHARGES] = (u.k[CHARGES] as number) - 1;
  if (u.cds[LEAP] === 0) u.cds[LEAP] = l.every;
  const r = VELA.roots;
  s.zones.push({ id: s.nextId++, kind: 'velaRoots', x: e.x, y: e.y, radius: r.radius, cells: [], ticks: r.ticks, every: 1, damage: r.damage, slowBp: 0, pid: '', warn: r.arm });
  e.anim = 'walk';
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: e.x + u.vx * u.busy, ty: e.y + u.vy * u.busy, kind: 'velaLeap' });
  said(s, e, u, 'Leap');
  return true;
};

const leapStep = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const nx = e.x + u.vx;
  const ny = e.y + u.vy;
  if (!freeAt(s, nx, ny)) u.busy = 1;
  else {
    e.x = nx;
    e.y = ny;
  }
  e.anim = 'walk';
  if (--u.busy === 0) {
    e.next = -1;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: 700, kind: 'velaLand' });
  }
  return true;
};

/** Her roots and her rain on the ground, each tick. */
const ground = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  let spent = false;
  for (const z of s.zones) {
    if (z.kind === 'velaRoots') {
      if ((z.warn ?? 0) > 0) {
        z.warn = (z.warn ?? 0) - 1;
        continue;
      }
      const caught = heroesNear(s, z.x, z.y, z.radius).filter((h) => holdable(s, h));
      if (!caught.length) continue;
      for (const h of caught) {
        hurt(s, e, u, h, z.damage, VELA.roots.maxHpBp);
        if (!standing(h)) continue;
        h.stun = imax(h.stun, VELA.roots.hold);
        held(s, h);
      }
      z.ticks = 0;
      spent = true;
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'velaRoots' });
    } else if (z.kind === 'velaRain' && z.ticks % z.every === 0) {
      for (const h of heroesNear(s, z.x, z.y, z.radius)) hurt(s, e, u, h, z.damage, VELA.rain.maxHpBp);
      for (const st of s.structures) {
        if (!isTower(st)) continue;
        const c = at(st);
        if (withinRange(z.x, z.y, c.x, c.y, z.radius)) damageStructure(s, st, mulBp(st.maxHp, VELA.rain.towerBp), e);
      }
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'velaVolley' });
    }
  }
  if (spent) s.zones = s.zones.filter((z) => z.ticks > 0);
};

/** The tower with the most towers around it within `range` of her (her rain's aim when no hero is in reach). */
const towers = (s: BattleState, e: Enemy, range: number, r: number): { x: number; y: number } | null => {
  let best: { x: number; y: number; n: number } | null = null;
  for (const st of s.structures) {
    if (!isTower(st)) continue;
    const c = at(st);
    if (!withinRange(e.x, e.y, c.x, c.y, range)) continue;
    let n = 0;
    for (const o of s.structures) if (isTower(o) && withinRange(c.x, c.y, at(o).x, at(o).y, r)) n++;
    if (!best || n > best.n) best = { x: c.x, y: c.y, n };
  }
  return best;
};

export const vela: Kit = {
  init: (_s, _e, u) => {
    u.cds = [VELA.arrow.every >> 2, 0];
    u.k = [VELA.leap.charges[0] ?? 1];
  },
  busy: leapStep,
  act: (s, e, u) => {
    if (leap(s, e, u)) return true;
    if (u.cds[ARROW] === 0 && arrow(s, e, u)) return true;
    return shoot(s, e, u, VELA.range, VELA.still.ticks, 'velaShot', vela.strike);
  },
  // The rain over the tightest group (two smaller ones over two groups in her last phase); with no hero in reach, over her towers.
  aim: (s, e, u) => {
    const r = VELA.rain;
    const R = e.phase >= 2 ? r.smallRadius : r.radius;
    const first = crowd(s, e, r.range, R) ?? towers(s, e, r.range, R);
    if (!first) return false;
    u.spots = [{ x: first.x, y: first.y, r: R }];
    if (e.phase >= 2) {
      const second = crowd(s, e, r.range, R, [first], r.apart);
      if (second) u.spots.push({ x: second.x, y: second.y, r: R });
    }
    u.castA = e.facing;
    said(s, e, u, 'Rain', first.x, first.y);
    return true;
  },
  land: (s, e, u) => {
    const r = VELA.rain;
    for (const p of u.spots) s.zones.push({ id: s.nextId++, kind: 'velaRain', x: p.x, y: p.y, radius: p.r, cells: [], ticks: r.ticks, every: r.every, damage: r.damage, slowBp: 0, pid: '' });
    ev(s, { t: 'boom', x: e.x, y: e.y, r: u.spots[0]?.r ?? r.radius, kind: 'velaRainFall' });
  },
  // Each shot goes on through the heroes behind the one she aims at, to her range.
  strike: (s, e, h, dmg) => {
    damageHero(s, h, shot(h, dmg), e, undefined, true);
    const dx = h.x - e.x;
    const dy = h.y - e.y;
    const d = isqrt(dx * dx + dy * dy);
    if (d === 0) return;
    for (const o of s.heroes) {
      if (o === h || !standing(o) || !onLine(e.x, e.y, dx, dy, d, o.x, o.y, d - 300, VELA.range + 500, VELA.pierce.width)) continue;
      damageHero(s, o, shot(o, dmg), e, undefined, true);
    }
  },
  phase: (_s, e, u) => {
    u.k[CHARGES] = VELA.leap.charges[e.phase] ?? 1;
  },
  status: (s, e, u) => {
    trackStill(s);
    ground(s, e, u);
  },
};
